/**
 * ProductGridBlock
 * Props-injected: requires `fetchProducts` prop.
 * fetchProducts: async ({ limit, categorySlug }) => Product[]
 *
 * Site Builder persists the content_schema keys `category` and `limit`.
 * Older content may use `categorySlug` and `displayCount`.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Section, Container, BlockTitle, BlockSubtitle, BlockHeader, BtnPrimary, t } from '../styles/shared';
import { resolveMediaServeUrl } from '../utils/mediaServeUrls';

const Grid = styled.div`
  display: grid; gap: 24px; margin-bottom: 32px;
  grid-template-columns: ${({ $cols }) => `repeat(${$cols}, 1fr)`};
  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Card = styled(Link)`
  display: block; background: #fff; border: 1px solid ${t.border};
  border-radius: 8px; overflow: hidden; text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(0,0,0,0.1); }
`;

const ImgWrap = styled.div`
  aspect-ratio: 4 / 3; background: ${t.bgAlt}; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const NoImg = styled.div`
  width: 100%; height: 100%; display: flex; align-items: center;
  justify-content: center; color: ${t.textLight}; font-size: 0.875rem;
`;

const Info = styled.div` padding: 16px; `;

const ProductName = styled.h3`
  margin: 0 0 8px 0; font-family: ${t.fontHeading};
  font-size: 1.125rem; font-weight: 600; color: ${t.text};
`;

const Desc = styled.p`
  margin: 0 0 12px 0; font-size: 0.875rem; color: ${t.textLight}; line-height: 1.5;
`;

const Price = styled.div`
  font-size: 1.125rem; font-weight: 600; color: ${t.primary};
`;

const Unit = styled.span`
  font-weight: 400; font-size: 0.875rem; color: ${t.textLight};
`;

const Loading = styled.div`
  text-align: center; padding: 48px; color: ${t.textLight};
`;

const CTA = styled.div` margin-top: 32px; text-align: center; `;

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(p);

/** Schema keys (`category`, `limit`) win. Legacy keys are the fallback. */
export function resolveProductQuery(content = {}) {
  const categorySlug = String(content.category || content.categorySlug || '').trim();
  const rawCount = content.limit ?? content.displayCount ?? 6;
  const parsed = Number(rawCount);
  const displayCount = Number.isFinite(parsed) && parsed > 0 ? parsed : 6;
  return { categorySlug, displayCount };
}

const ProductGridBlock = ({ content = {}, settings = {}, fetchProducts, tenantSlug, apiBase }) => {
  const {
    title = '', subtitle = '',
    showButton = true, buttonText = 'View All Products',
    buttonLink = '/shopping', columns = 3
  } = content;
  const { categorySlug, displayCount } = resolveProductQuery(content);
  const { backgroundColor = '', padding = 'large' } = settings;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fetchProducts) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetchProducts({ limit: displayCount, categorySlug })
      .then(data => { if (!cancelled) setProducts(data || []); })
      .catch(err => { if (!cancelled) console.error(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [displayCount, categorySlug, fetchProducts]);

  if (loading) return (
    <Section $padding={padding} $bgColor={backgroundColor}>
      <Container>
        {title && <BlockTitle style={{ textAlign: 'center' }}>{title}</BlockTitle>}
        <Loading>Loading products…</Loading>
      </Container>
    </Section>
  );

  if (!products.length) return (
    <Section $padding={padding} $bgColor={backgroundColor}>
      <Container>
        {(title || subtitle) && (
          <BlockHeader $center>
            {title && <BlockTitle style={{ textAlign: 'center' }}>{title}</BlockTitle>}
            {subtitle && <BlockSubtitle style={{ textAlign: 'center' }}>{subtitle}</BlockSubtitle>}
          </BlockHeader>
        )}
        <Loading style={{ border: '2px dashed #e0d5c7', borderRadius: '8px', color: '#9ca3af' }}>
          🛒 No products found.
          {categorySlug ? ` No products in category "${categorySlug}".` : ' Add products in Inventory → Products.'}
        </Loading>
      </Container>
    </Section>
  );

  return (
    <Section $padding={padding} $bgColor={backgroundColor}>
      <Container>
        {(title || subtitle) && (
          <BlockHeader $center>
            {title && <BlockTitle style={{ textAlign: 'center' }}>{title}</BlockTitle>}
            {subtitle && <BlockSubtitle style={{ textAlign: 'center' }}>{subtitle}</BlockSubtitle>}
          </BlockHeader>
        )}
        <Grid $cols={Math.min(columns, products.length)}>
          {products.map(p => (
            <Card key={p.id} to={`/product/${p.id}`}>
              <ImgWrap>
                {p.image_url
                  ? <img src={resolveMediaServeUrl(p.image_url, tenantSlug, apiBase)} alt={p.name} loading="lazy" />
                  : <NoImg>No Image</NoImg>
                }
              </ImgWrap>
              <Info>
                <ProductName>{p.name}</ProductName>
                {p.description && (
                  <Desc>
                    {p.description.length > 80 ? `${p.description.slice(0, 80)}…` : p.description}
                  </Desc>
                )}
                <Price>
                  {fmt(p.price)}
                  {p.unit && <Unit> / {p.unit}</Unit>}
                </Price>
              </Info>
            </Card>
          ))}
        </Grid>
        {showButton && (
          <CTA>
            <BtnPrimary as={Link} to={buttonLink}>{buttonText}</BtnPrimary>
          </CTA>
        )}
      </Container>
    </Section>
  );
};

export default ProductGridBlock;
